<template>
  <BaseDemandCard
    :demand="demand"
    :demand-color="demandColor"
    :color="mainColor"
    :is-overlay-active="isOverlayActive"
    @click="$_goToDemandDetails({ isOverlayActive, demand })"
  >
    <template #top>
      <section class="demand-card-top">
        <div class="demand-card-top__status">
          <TemporaryDemandStatus :demand="demand" :color="demandColor" />
          <Avatar
            v-for="(candidate, index) in demand.getCandidates()"
            :key="index"
            class="demand-card-top__avatar"
            :picture="candidate.getPicture()"
            :size="$_avatarSize"
          />
        </div>

        <div>
          <div>
            <p class="demand-card-top__candidate-wished">
              {{ $tc("ETAB.PAGES.RECRUITMENT.TILE.@CANDIDATE_WISHED", { count: demand.getWishedCandidateAmount() }) }}
            </p>
            <p v-if="demand.getRelaunchInfo() && demand.getRelaunchInfo().isAuto()" class="demand-card-top__relaunch">
              {{ $t("ETAB.PAGES.RECRUITMENT.TILE.@RELAUNCH_OFFER") }}
            </p>
          </div>
        </div>
      </section>
    </template>

    <template #overlay>
      <section class="demand-card-overlay">
        <p>
          <span v-if="demand.getCanceledCandidates()">{{ $t("ETAB.PAGES.RECRUITMENT.TILE.@DO_YOU_WANT_TO_RESTART") }}</span>
          <span v-else>{{ $filters.whitespacedPunctuation($t("ETAB.PAGES.RECRUITMENT.TILE.@EXTRA_CANCELED_DO_YOU_WANT_TO_RESTART")) }}</span>
        </p>
        <div>
          <DemandCardButton :color="mainColor" variant="filled" @click="$_cancelDemandRelaunch(demand)">
            {{ $t("ETAB.BUTTONS.@NO") }}
          </DemandCardButton>
          <DemandCardButton :color="mainColor" variant="filled" @click="$_relaunchDemand(demand)">
            {{ $t("ETAB.BUTTONS.@YES") }}
          </DemandCardButton>
        </div>
      </section>
    </template>

    <template #middle>
      <section class="demand-card-middle">
        <div>
          <DemandDates
            :start-date="demand.getStartDate()"
            :end-date="demand.getEndDate()"
            :start-time="demand.getStartTime()"
            :is-one-day="demand.isOneDay()"
          />
          <span class="demand-card-middle__address">{{ demand.getMissionAddress() }}</span>
        </div>
      </section>
    </template>

    <template #bottom>
      <section v-if="!isOverlayActive" class="demand-card-bottom">
        <DemandCardButton
          v-if="demand.getTemplateId()"
          :color="mainColor"
          variant="outlined"
          @click="$_duplicateDemand(demand.getTemplateId(), posthogDemandCreationEventsEnabled)"
        >
          {{ $t("ETAB.PAGES.RECRUITMENT.TILE.@DUPLICATE_DEMAND") }}
        </DemandCardButton>
        <DemandCardButton :color="mainColor" variant="filled" @click="$_goToDemandDetails({ isOverlayActive, demand })">
          {{ $t("ETAB.PAGES.RECRUITMENT.TILE.@OPEN_DEMAND") }}
        </DemandCardButton>
      </section>
    </template>
  </BaseDemandCard>
</template>

<script>
  import { RecruitmentDashboardModel } from "@core/models";
  import { dashboardDemandMixin } from "@etab/mixins";
  import { CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE } from "@core/store/types";
  import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
  import { FEATURE_TOGGLES } from "@core/constants";

  import BaseDemandCard from "./BaseDemandCard.component.vue";
  import DemandCardButton from "./card/Button.component.vue";
  import DemandDates from "./card/DemandDates.component.vue";
  import TemporaryDemandStatus from "./card/TemporaryDemandStatus.component.vue";

  export default {
    components: {
      BaseDemandCard,
      DemandCardButton,
      DemandDates,
      TemporaryDemandStatus,
    },
    mixins: [dashboardDemandMixin],
    props: {
      demand: {
        type: Object,
        default: () => new RecruitmentDashboardModel(),
      },
    },
    computed: {
      mainColor() {
        return this.$_colors.interim; // extra-green,
      },
      demandColor() {
        if (this.demand.isFilled()) {
          return this.$_colors.success;
        }
        if (this.demand.hasCandidate()) {
          return this.$_colors.third;
        }
        return this.mainColor;
      },
      isOverlayActive() {
        return this.demand.isExpired() && !this.demand.getSelectedCandidateAmount();
      },
      posthogDemandCreationEventsEnabled() {
        const featureTogglesStore = useFeatureTogglesStore();
        return featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_TEMPLATE_FUNNEL);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .demand-card {
    &-top {
      display: flex;
      justify-content: space-between;

      &__avatar {
        margin: 0 2px;
      }

      &__status {
        display: flex;
      }

      div {
        color: $primary;
        text-align: right;
      }

      &__relaunch {
        font-weight: bold;
      }

      &__candidate-wished,
      &__relaunch {
        margin: 0;
      }
    }
    &-middle {
      display: flex;
      align-items: center;
      width: 100%;
      color: $primary;
    }
    &-bottom {
      display: flex;
      justify-content: center;
      > * {
        margin: 0 0.5rem;
        &:first-child {
          margin-left: 0;
        }
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  .demand-card-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    span {
      font-size: 16px;
    }
    div {
      display: flex;
      width: 80%;
      > * {
        margin: 0 0.5rem;
        &:first-child {
          margin-left: 0;
        }
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
</style>
